/**
 * Events Journal — `/events`
 *
 * A chronological record of every activity the family has completed.
 * Each entry shows the activity title, completion date, category,
 * star rating, the parent's notes, and the reminiscing prompt from
 * the template (so the parent can revisit the conversation starter
 * even after the event).
 *
 * This is the "memory book" surface — tangible proof the family is
 * making it count. The data comes from activity_suggestions with
 * status='completed', joined to activity_templates for the
 * research-backed metadata.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { BrandMark } from "@/components/BrandMark";
import { LoadingShell } from "@/components/LoadingShell";
import { Wordmark } from "@/components/Wordmark";
import { EventPhotoGallery } from "@/features/activities/EventPhotoGallery";
import {
  useCompletedActivities,
  useUpdateSuggestion,
  type ActivitySuggestion,
} from "@/features/activities/useActivities";

const CATEGORY_LABELS: Record<string, string> = {
  nature: "Nature",
  cooking: "Cooking",
  creative: "Creative",
  mastery: "Mastery",
  ritual: "Ritual",
  adventure: "Adventure",
  service: "Service",
  storytelling: "Storytelling",
  physical: "Physical",
  one_on_one: "One-on-one",
};

export function EventsJournalPage() {
  const { data: events, isLoading, error } = useCompletedActivities();

  if (isLoading) {
    return <LoadingShell message="Loading your family journal..." />;
  }

  return (
    <main className="max-w-[780px] mx-auto px-6 md:px-11 pt-14 md:pt-20 pb-24 flex flex-col gap-10">
      <header className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <BrandMark />
          <Wordmark size="hero" />
        </div>
        <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
          Family journal
        </div>
        <h1 className="font-serif text-[24px] md:text-display-md text-ink">
          Everything you've done{" "}
          <em className="italic font-normal text-coral-deep">together</em>
        </h1>
        <p className="font-sans text-[14px] text-ink-muted max-w-[560px] leading-[1.6]">
          Every activity your family has completed, newest first. The
          reminiscing prompts are here too — revisit them anytime to
          strengthen the memory.
        </p>
      </header>

      {error !== null && error !== undefined ? (
        <div
          role="alert"
          className="rounded-md border border-coral-deep/30 bg-coral-soft/40 px-5 py-4 font-sans text-[14px] text-coral-deep"
        >
          Couldn't load the journal. Refresh to try again.
        </div>
      ) : events !== undefined && events.length === 0 ? (
        <EmptyJournal />
      ) : (
        <div className="flex flex-col gap-5">
          {(events ?? []).map((event) => (
            <JournalEntry key={event.id} event={event} />
          ))}
        </div>
      )}
    </main>
  );
}

function JournalEntry({ event }: { event: ActivitySuggestion }) {
  const { template } = event;
  const peakMoment = event.personalized_peak_moment ?? template.peak_moment;
  const completedDate = event.completed_at
    ? new Date(event.completed_at)
    : null;

  return (
    <article className="rounded-lg bg-surface border border-edge px-5 md:px-6 py-5 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-sans text-[10px] uppercase tracking-[0.14em] font-semibold text-coral-deep bg-coral-soft/60 border border-coral-deep/20 rounded-pill px-2 py-0.5">
              {CATEGORY_LABELS[template.category] ?? template.category}
            </span>
            {completedDate !== null && (
              <span className="font-sans text-[12px] text-ink-muted">
                {completedDate.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
            {event.acted_by_name !== null && (
              <span className="font-sans text-[12px] text-ink-muted">
                by {event.acted_by_name}
              </span>
            )}
          </div>
          <h2 className="font-serif text-[18px] text-ink leading-snug">
            {template.title}
          </h2>
        </div>

        {event.rating !== null && (
          <div className="font-mono tabular-nums text-[16px] text-amber-deep shrink-0">
            {"★".repeat(event.rating)}
            {"☆".repeat(5 - event.rating)}
          </div>
        )}
      </div>

      {event.notes !== null && event.notes.length > 0 && (
        <div className="font-sans text-[14px] text-ink-soft leading-[1.6] bg-bg/50 rounded-md px-4 py-3 border border-edge-soft">
          {event.notes}
        </div>
      )}

      <EventPhotoGallery suggestionId={event.id} />

      <SharedAlbumLink event={event} />

      <div className="flex flex-col gap-3">
        <div className="rounded-md bg-amber-soft/30 border border-amber/15 px-4 py-3">
          <div className="font-sans text-[11px] uppercase tracking-[0.14em] font-semibold text-amber-deep mb-1">
            The moment to remember
          </div>
          <div className="font-sans text-[13px] text-ink-soft italic">
            {peakMoment}
          </div>
        </div>

        <div className="rounded-md bg-coral-soft/20 border border-coral-deep/10 px-4 py-3">
          <div className="font-sans text-[11px] uppercase tracking-[0.14em] font-semibold text-coral-deep mb-1">
            Revisit this conversation
          </div>
          <div className="font-sans text-[13px] text-ink-soft italic">
            {template.reminiscing_prompt}
          </div>
        </div>
      </div>
    </article>
  );
}

function SharedAlbumLink({ event }: { event: ActivitySuggestion }) {
  const update = useUpdateSuggestion();
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(event.photo_album_url ?? "");

  const handleSave = () => {
    const trimmed = url.trim();
    update.mutate({
      suggestionId: event.id,
      photo_album_url: trimmed || "",
    });
    setEditing(false);
  };

  if (event.photo_album_url !== null && event.photo_album_url.length > 0 && !editing) {
    return (
      <div className="flex items-center gap-3">
        <a
          href={event.photo_album_url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-sans text-[13px] text-coral-deep hover:underline"
        >
          Shared photo album ↗
        </a>
        <button
          type="button"
          onClick={() => { setUrl(event.photo_album_url ?? ""); setEditing(true); }}
          className="font-sans text-[11px] text-ink-dim hover:text-ink-muted"
        >
          Edit
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste Google Photos or Apple album link"
          className="flex-1 font-sans text-[13px] text-ink bg-surface border border-edge rounded-md px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={update.isPending}
          className="px-3 py-2 rounded-md font-sans text-[12px] font-semibold text-white bg-coral-deep disabled:opacity-60"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="font-sans text-[12px] text-ink-muted"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="self-start font-sans text-[12px] text-ink-muted hover:text-coral-deep"
    >
      + Add shared photo album link
    </button>
  );
}

function EmptyJournal() {
  return (
    <div className="rounded-lg bg-surface border border-edge shadow-sm px-6 md:px-8 py-10 flex flex-col gap-4 items-start max-w-[560px]">
      <h2 className="font-serif text-[24px] text-ink leading-snug">
        Your journal is{" "}
        <em className="italic font-normal text-coral-deep">waiting</em>.
      </h2>
      <p className="font-sans text-[14px] text-ink-muted leading-[1.6]">
        Complete an activity from the dashboard and it'll show up here —
        a growing record of the experiences you're building with your
        kids, one moment at a time.
      </p>
      <Link
        to="/dashboard"
        className="mt-2 inline-flex items-center px-5 py-2 rounded-md font-sans text-[14px] font-semibold text-white shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        style={{
          background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
          boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
        }}
      >
        Back to dashboard
      </Link>
    </div>
  );
}
